import React, { useEffect, useRef, useState } from "react";

import deeperInvitation from "../../../assets/images/projects/sedap/deeper-invitation.png";
import feedbackGovernance from "../../../assets/images/projects/sedap/feedback-governance.png";
import homepageBanner from "../../../assets/images/projects/sedap/homepage-banner.png";
import infoboxSystem from "../../../assets/images/projects/sedap/infobox-system.png";
import missionAssistance from "../../../assets/images/projects/sedap/mission-assistance.png";

import { ProjectDetailProps } from "../props";
import {
  FoggChart,
  FoggCurve,
  FoggNote,
  FoggPoint,
  Hero,
  HeroCopy,
  HeroInner,
  HeroLoop,
  HeroLoopStep,
  HookLoop,
  HookStep,
  Lightbox,
  LightboxClose,
  ModelCopy,
  ModelHeader,
  ModelInner,
  ModelPanel,
  ModelSection,
  ModelTab,
  ModelTabs,
  Process,
  ProcessDetail,
  ProcessInner,
  ProcessRail,
  ProcessStep,
  ProposalBackCopy,
  ProposalCard,
  ProposalCardActions,
  ProposalCardButton,
  ProposalCardFace,
  ProposalCardInner,
  ProposalCardPeek,
  ProposalCarouselActions,
  ProposalCarouselButton,
  ProposalCarouselCount,
  ProposalCarouselFooter,
  ProposalCarouselHeader,
  ProposalCarouselTrack,
  ProposalDot,
  ProposalDots,
  ProposalFlipHint,
  ProposalPurpose,
  ProposalSwipeHint,
  Question,
  RelationshipCopy,
  RelationshipFigure,
  RelationshipInner,
  RelationshipSection,
  SectionHeader,
} from "./sedap-style";

type ModelKey = "fogg" | "hook";
type ProposalKey = "prompt" | "tray" | "integration" | "program" | "governance";

const processSteps = [
  {
    title: "Read the existing signals",
    short: "Complaints and broad scores lacked product context",
    detail:
      "The team already received complaints and broad satisfaction scores. These signals could reveal dissatisfaction, but they did not explain which product moment caused it, what the user was trying to do, or what needed to improve.",
  },
  {
    title: "Define the design tension",
    short: "Learn more without overwhelming users",
    detail:
      "The feedback had to be discoverable across different product moments, but it could not interrupt a purchase or compete with other survey requests. The problem became how to earn a useful response with the least necessary effort.",
  },
  {
    title: "Understand participation",
    short: "Separate motivation, ability, prompts, and value",
    detail:
      "I used the Fogg Behavior Model to understand whether the design should simplify the action or strengthen motivation. The Hook Model helped me explore what users could receive in return while protecting their choice to participate.",
  },
  {
    title: "Separate the feedback jobs",
    short: "Experience, clarity, and future concepts",
    detail:
      "The brief contained three different learning needs: improve an experience, check whether complex product information was clear, and explore a future concept. Separating them prevented one questionnaire from trying to answer everything.",
  },
  {
    title: "Propose and govern the features",
    short: "Connect each entry point to a reason and audience",
    detail:
      "I translated the logic into in-product prompts, expanded response states, existing journey integrations, and a voluntary feedback program. The governance connected every proposal to its situation, touchpoint, purpose, and target users.",
  },
];

const proposals = {
  prompt: {
    label: "In-product prompt",
    purpose: "Experience and product clarity",
    moment: "After a meaningful interaction or product change",
    audience: "Users who recently experienced the relevant moment",
    title: "Start with a lightweight prompt near the experience.",
    copy: "I proposed an inline entry point that could begin with a rating or simple question, then open more detail only when the response required it.",
    rationale:
      "Product managers could connect the answer to a known moment. Users did not have to reconstruct the experience later.",
    image: infoboxSystem,
    caption: "Proposed infobox, rating, and progressive question states.",
  },
  tray: {
    label: "Expanded response",
    purpose: "Product clarity",
    moment: "When users read complex information or show uncertainty",
    audience:
      "Users who viewed the relevant content or reached a key decision point",
    title: "Use a deeper tray only when the first answer needs context.",
    copy: "The first response stays simple. A richer tray can collect the reason, invite an optional explanation, or ask whether the user is willing to continue into research.",
    rationale:
      "The business receives more useful context without turning every visit into a long questionnaire.",
    image: deeperInvitation,
    caption: "Proposed tray and voluntary follow-up interaction.",
  },
  integration: {
    label: "Existing journey integration",
    purpose: "Experience and product clarity",
    moment: "After a support request or a difficult product moment",
    audience: "Users who requested assistance or completed a support flow",
    title: "Connect feedback to the support already present in the journey.",
    copy: "I placed feedback after an assistance interaction, where it could reveal whether the product explanation and the support experience worked together.",
    rationale:
      "This gave product and business partners a joined view of the journey instead of treating service feedback as a separate score.",
    image: missionAssistance,
    caption:
      "Proposed feedback entry point within an existing assistance flow.",
  },
  program: {
    label: "Feedback program invitation",
    purpose: "Experiment with a new concept",
    moment: "When the team needs learning beyond a single transaction",
    audience: "Selected users who voluntarily join a deeper study",
    title: "Invite willing users into a longer learning relationship.",
    copy: "For questions that could not be answered through a small prompt, I explored an opt-in program with an intentional invitation, clear expectations, and a visible return for participation.",
    rationale:
      "This remained a longer-term direction. It gave stakeholders a way to discuss deeper research without mixing it with the core feedback components.",
    image: homepageBanner,
    caption: "Invitation concepts for a voluntary feedback program.",
  },
  governance: {
    label: "Governance and prioritization",
    purpose: "Experience, clarity, and future concepts",
    moment: "Across repeated learning cycles",
    audience: "Relevant participants selected by the question being explored",
    title: "Govern the full system instead of launching isolated surveys.",
    copy: "The governance connected each product question to a moment, purpose, interaction pattern, and audience. It also showed how lightweight and deeper feedback products could work as one portfolio.",
    rationale:
      "This was the shared decision tool. Product managers and business partners could review the logic, challenge the proposal, and prioritize with the same context.",
    image: feedbackGovernance,
    caption:
      "The governance output for choosing and prioritizing the proposed feedback products.",
  },
} as Record<
  ProposalKey,
  {
    label: string;
    purpose: string;
    moment: string;
    audience: string;
    title: string;
    copy: string;
    rationale: string;
    image: string;
    caption: string;
  }
>;

const proposalOrder: ProposalKey[] = [
  "governance",
  "prompt",
  "tray",
  "integration",
  "program",
];

const proposalAccents: Record<ProposalKey, string> = {
  prompt: "#77e1cf",
  tray: "#ff8a76",
  integration: "#4ec7e4",
  program: "#a987ff",
  governance: "#f0ca69",
};

const HeroVisual = () => (
  <HeroLoop aria-label="How I approached the SEDAP brief">
    <HeroLoopStep tone="rgba(0,151,208,.52)">
      <span>01</span>
      <strong>Explore where feedback belongs</strong>
    </HeroLoopStep>
    <HeroLoopStep tone="rgba(255,100,75,.58)">
      <span>02</span>
      <strong>Connect feedback to the experience</strong>
    </HeroLoopStep>
    <HeroLoopStep tone="rgba(116,56,218,.5)">
      <span>03</span>
      <strong>Understand participation</strong>
    </HeroLoopStep>
    <HeroLoopStep tone="rgba(66,197,176,.48)">
      <span>04</span>
      <strong>Turn the thinking into concepts and guidelines</strong>
    </HeroLoopStep>
  </HeroLoop>
);

const ThoughtProcess = () => {
  const [active, setActive] = useState(0);

  return (
    <Process>
      <ProcessInner>
        <SectionHeader>
          <h3>The first task was to decide what the system needed to learn.</h3>
          <p>
            Follow the decisions that moved the project from broad feedback
            signals to governed product proposals.
          </p>
        </SectionHeader>
        <ProcessRail aria-label="How the feedback system took shape">
          {processSteps.map((step, index) => (
            <ProcessStep
              key={step.title}
              type="button"
              aria-pressed={active === index}
              onMouseEnter={() => setActive(index)}
              onFocus={() => setActive(index)}
              onClick={() => setActive(index)}
            >
              <span>0{index + 1}</span>
              <strong>{step.title}</strong>
              <small>{step.short}</small>
            </ProcessStep>
          ))}
        </ProcessRail>
        <ProcessDetail aria-live="polite">
          <h4>{processSteps[active].title}</h4>
          <p>{processSteps[active].detail}</p>
        </ProcessDetail>
      </ProcessInner>
    </Process>
  );
};

const BehaviorModels = () => {
  const [active, setActive] = useState<ModelKey>("fogg");
  const [foggPoint, setFoggPoint] = useState(0);
  const [hookPoint, setHookPoint] = useState(0);
  const foggNotes = [
    "Facilitator: reduce effort when users want to help but the task feels difficult.",
    "Spark: make the value clearer when ability is enough but motivation is low.",
    "Signal: use a timely reminder when motivation and ability are already present.",
  ];
  const hookNotes = [
    "Trigger: invite users from a relevant experience or an intentional program.",
    "Action: start with the smallest meaningful response.",
    "Reward: recognize the contribution with useful or personal value.",
    "Investment: let willing users opt into a deeper relationship, never force it.",
  ];

  return (
    <ModelSection>
      <ModelInner>
        <ModelHeader>
          <h3>Two models helped me answer two different questions.</h3>
          <p>
            Fogg helped me design the moment. The Hook Model helped me design
            the relationship around it.
          </p>
        </ModelHeader>
        <ModelTabs aria-label="Behavior models">
          <ModelTab
            type="button"
            aria-pressed={active === "fogg"}
            onClick={() => setActive("fogg")}
          >
            Fogg Behavior Model
          </ModelTab>
          <ModelTab
            type="button"
            aria-pressed={active === "hook"}
            onClick={() => setActive("hook")}
          >
            Hook Model
          </ModelTab>
        </ModelTabs>
        <ModelPanel>
          <ModelCopy>
            {active === "fogg" ? (
              <>
                <span>Motivation + ability + prompt</span>
                <h4>
                  A prompt works only when the moment supports the behavior.
                </h4>
                <p>
                  Users can intend to help and still do nothing. I used the
                  model to decide whether the design should simplify the task,
                  build motivation, or simply remind at the right time.
                </p>
              </>
            ) : (
              <>
                <span>Trigger + action + reward + investment</span>
                <h4>A feedback loop needs a fair value exchange.</h4>
                <p>
                  I used the Hook Model to explore repeat engagement, but kept
                  autonomy as a design requirement. A user could give one quick
                  response, choose a deeper task, or leave without friction.
                </p>
              </>
            )}
          </ModelCopy>
          {active === "fogg" ? (
            <FoggChart aria-label="Interactive Fogg Behavior Model">
              <FoggCurve />
              <FoggPoint
                type="button"
                x="31%"
                y="39%"
                tone="rgba(38,170,190,.78)"
                aria-pressed={foggPoint === 0}
                onClick={() => setFoggPoint(0)}
              >
                <strong>Facilitator</strong>
                <span>Make it simple</span>
              </FoggPoint>
              <FoggPoint
                type="button"
                x="58%"
                y="66%"
                tone="rgba(214,180,38,.75)"
                aria-pressed={foggPoint === 1}
                onClick={() => setFoggPoint(1)}
              >
                <strong>Spark</strong>
                <span>Create motivation</span>
              </FoggPoint>
              <FoggPoint
                type="button"
                x="78%"
                y="28%"
                tone="rgba(219,96,105,.75)"
                aria-pressed={foggPoint === 2}
                onClick={() => setFoggPoint(2)}
              >
                <strong>Signal</strong>
                <span>Remind me</span>
              </FoggPoint>
              <FoggNote aria-live="polite">{foggNotes[foggPoint]}</FoggNote>
            </FoggChart>
          ) : (
            <HookLoop aria-label="Interactive Hook Model">
              {[
                ["Trigger", "Relevant invitation", "rgba(11,151,207,.66)"],
                ["Action", "Smallest useful response", "rgba(255,102,81,.68)"],
                [
                  "Investment",
                  "Opt into deeper participation",
                  "rgba(52,191,166,.62)",
                ],
                [
                  "Reward",
                  "Value that recognizes effort",
                  "rgba(112,55,219,.62)",
                ],
              ].map((item, index) => (
                <HookStep
                  key={item[0]}
                  type="button"
                  tone={item[2]}
                  aria-pressed={hookPoint === index}
                  onClick={() => setHookPoint(index)}
                >
                  <span>0{index + 1}</span>
                  <strong>{item[0]}</strong>
                  <small>
                    {hookPoint === index ? hookNotes[index] : item[1]}
                  </small>
                </HookStep>
              ))}
            </HookLoop>
          )}
        </ModelPanel>
      </ModelInner>
    </ModelSection>
  );
};

const RelationshipOutcome = () => {
  const [deckOrder, setDeckOrder] = useState<ProposalKey[]>(proposalOrder);
  const [lightboxKey, setLightboxKey] = useState<ProposalKey | null>(null);
  const [flippedKey, setFlippedKey] = useState<ProposalKey | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragX, setDragX] = useState(0);
  const [exitDirection, setExitDirection] = useState<"left" | "right" | null>(
    null,
  );
  const dragRef = useRef({ active: false, startX: 0, moved: false });
  const activeKey = deckOrder[0];

  const sendFrontToBack = (direction: "left" | "right" = "left") => {
    if (exitDirection) return;
    setIsDragging(false);
    setExitDirection(direction);
  };

  const finishCycle = () => {
    if (!exitDirection) return;
    setDeckOrder((current) => [...current.slice(1), current[0]]);
    setFlippedKey(null);
    setDragX(0);
    setExitDirection(null);
  };

  const bringToFront = (key: ProposalKey) => {
    if (key === activeKey || exitDirection) return;
    setDeckOrder((current) => {
      const index = current.indexOf(key);
      return [...current.slice(index), ...current.slice(0, index)];
    });
    setFlippedKey(null);
    setDragX(0);
  };

  const toggleCard = (key: ProposalKey) => {
    if (key !== activeKey || exitDirection) return;
    setFlippedKey((current) => (current === key ? null : key));
  };

  const finishDrag = (event: React.PointerEvent<HTMLElement>) => {
    if (!dragRef.current.active) return;
    const finalOffset = event.clientX - dragRef.current.startX;
    dragRef.current.active = false;
    setIsDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (Math.abs(finalOffset) >= 72) {
      sendFrontToBack(finalOffset < 0 ? "left" : "right");
    } else {
      setDragX(0);
    }
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setLightboxKey(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      <RelationshipSection>
        <RelationshipInner>
          <SectionHeader>
            <h3>Five proposals in cards, one connected feedback system.</h3>
            <p>
              The governance opens the bigger picture. Swipe through the deck to
              see how each feature answered a different part of it.
            </p>
          </SectionHeader>
          <ProposalCarouselHeader>
            <ProposalSwipeHint>
              <strong>Swipe to continue</strong>
              <span>It moves to the back and the deck continues.</span>
            </ProposalSwipeHint>
            <ProposalFlipHint
              type="button"
              onClick={() => toggleCard(activeKey)}
              aria-pressed={flippedKey === activeKey}
            >
              {flippedKey === activeKey
                ? "Showing details · Tap to return"
                : "Tap the card to see details"}
            </ProposalFlipHint>
            <ProposalCarouselActions>
              <ProposalCarouselCount aria-live="polite">
                0{proposalOrder.indexOf(activeKey) + 1} / 0
                {proposalOrder.length}
              </ProposalCarouselCount>
              <ProposalCarouselButton
                type="button"
                onClick={() => sendFrontToBack("left")}
                aria-label="Move the front card to the back"
              >
                →
              </ProposalCarouselButton>
            </ProposalCarouselActions>
          </ProposalCarouselHeader>
          <ProposalCarouselTrack
            $dragging={isDragging}
            role="region"
            aria-roledescription="card deck"
            aria-label="Proposed feedback features"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "ArrowRight") {
                event.preventDefault();
                sendFrontToBack("left");
              }
              if (event.key === "ArrowLeft") {
                event.preventDefault();
                sendFrontToBack("right");
              }
            }}
          >
            {deckOrder.map((key, depth) => {
              const item = proposals[key];
              const isFront = depth === 0;
              const isFlipped = flippedKey === key;
              const sequence = proposalOrder.indexOf(key);
              return (
                <ProposalCard
                  key={key}
                  data-proposal-card
                  $accent={proposalAccents[key]}
                  $depth={depth}
                  $front={isFront}
                  $dragging={isFront && isDragging}
                  $offset={isFront ? dragX : 0}
                  $exitDirection={isFront ? exitDirection : null}
                  role="group"
                  aria-roledescription="card"
                  aria-label={`${item.label}, ${sequence + 1} of ${proposalOrder.length}`}
                  aria-hidden={!isFront}
                  onClick={(event) => {
                    if (
                      !isFront ||
                      (event.target as HTMLElement).closest("button")
                    ) {
                      return;
                    }
                    if (dragRef.current.moved) {
                      dragRef.current.moved = false;
                      return;
                    }
                    toggleCard(key);
                  }}
                  onPointerDown={(event) => {
                    if (
                      !isFront ||
                      exitDirection ||
                      event.button !== 0 ||
                      (event.target as HTMLElement).closest("button")
                    ) {
                      return;
                    }
                    dragRef.current = {
                      active: true,
                      startX: event.clientX,
                      moved: false,
                    };
                    setIsDragging(true);
                    event.currentTarget.setPointerCapture(event.pointerId);
                  }}
                  onPointerMove={(event) => {
                    if (!isFront || !dragRef.current.active) return;
                    const offset = event.clientX - dragRef.current.startX;
                    if (Math.abs(offset) > 6) dragRef.current.moved = true;
                    setDragX(offset);
                  }}
                  onPointerUp={finishDrag}
                  onPointerCancel={finishDrag}
                  onTransitionEnd={(event) => {
                    if (
                      isFront &&
                      exitDirection &&
                      event.target === event.currentTarget &&
                      event.propertyName === "transform"
                    ) {
                      finishCycle();
                    }
                  }}
                >
                  <ProposalCardPeek
                    $visible={!isFront}
                    $accent={proposalAccents[key]}
                  >
                    0{sequence + 1} · {item.label}
                  </ProposalCardPeek>
                  <ProposalCardInner $flipped={isFlipped}>
                    <ProposalCardFace
                      $accent={proposalAccents[key]}
                      aria-hidden={isFlipped}
                    >
                      <RelationshipCopy>
                        <span>
                          0{sequence + 1} · {item.label}
                        </span>
                        <h4>{item.title}</h4>
                        <p>{item.copy}</p>
                        <ProposalCardActions>
                          <ProposalCardButton
                            type="button"
                            $primary
                            $mobileHidden
                            onClick={() => toggleCard(key)}
                            tabIndex={isFront && !isFlipped ? 0 : -1}
                          >
                            View details
                          </ProposalCardButton>
                        </ProposalCardActions>
                      </RelationshipCopy>
                      <RelationshipFigure>
                        <img
                          src={item.image}
                          alt={item.caption}
                          loading="lazy"
                        />
                        <figcaption>
                          <span>{item.caption}</span>
                          <ProposalCardButton
                            type="button"
                            onClick={() => setLightboxKey(key)}
                            aria-label={`Open full visual for ${item.label}`}
                            tabIndex={isFront && !isFlipped ? 0 : -1}
                          >
                            Open visual
                          </ProposalCardButton>
                        </figcaption>
                      </RelationshipFigure>
                    </ProposalCardFace>
                    <ProposalCardFace
                      $back
                      $accent={proposalAccents[key]}
                      aria-hidden={!isFlipped}
                    >
                      <ProposalBackCopy $accent={proposalAccents[key]}>
                        <span>
                          0{sequence + 1} · {item.label}
                        </span>
                        <ProposalPurpose>{item.purpose}</ProposalPurpose>
                        <h4>Why this belongs in the system</h4>
                        <p>{item.copy}</p>
                        <dl>
                          <div>
                            <dt>User moment</dt>
                            <dd>{item.moment}</dd>
                          </div>
                          <div>
                            <dt>Recommended audience</dt>
                            <dd>{item.audience}</dd>
                          </div>
                        </dl>
                        <blockquote>{item.rationale}</blockquote>
                        <ProposalCardActions>
                          <ProposalCardButton
                            type="button"
                            $primary
                            onClick={() => toggleCard(key)}
                            tabIndex={isFront && isFlipped ? 0 : -1}
                          >
                            Back to summary
                          </ProposalCardButton>
                          <ProposalCardButton
                            type="button"
                            onClick={() => setLightboxKey(key)}
                            tabIndex={isFront && isFlipped ? 0 : -1}
                          >
                            Open visual
                          </ProposalCardButton>
                        </ProposalCardActions>
                      </ProposalBackCopy>
                    </ProposalCardFace>
                  </ProposalCardInner>
                </ProposalCard>
              );
            })}
          </ProposalCarouselTrack>
          <ProposalCarouselFooter>
            <ProposalDots aria-label="Choose a feedback proposal">
              {proposalOrder.map((key, index) => (
                <ProposalDot
                  key={key}
                  type="button"
                  $active={activeKey === key}
                  $accent={proposalAccents[key]}
                  onClick={() => bringToFront(key)}
                  aria-label={`Show ${proposals[key].label}`}
                  aria-current={activeKey === key ? "true" : undefined}
                />
              ))}
            </ProposalDots>
          </ProposalCarouselFooter>
        </RelationshipInner>
      </RelationshipSection>
      {lightboxKey && (
        <Lightbox
          role="dialog"
          aria-modal="true"
          aria-label="Full design visual"
        >
          <LightboxClose
            type="button"
            onClick={() => setLightboxKey(null)}
            autoFocus
          >
            Close
          </LightboxClose>
          <img
            src={proposals[lightboxKey].image}
            alt={proposals[lightboxKey].caption}
          />
        </Lightbox>
      )}
    </>
  );
};

export default {
  title: "Exploring a Systematic Feedback Loop for Traveloka Insurance",
  passwordProtected: true,
  timeline: "",
  context:
    "This project began with a business need to gather more useful feedback across Traveloka Insurance experiences. I worked as Design PIC and Visual Designer. My role was to structure the exploration, use behavioral models to examine participation, visualize possible feedback products, and organize the recommendations so product, design, and business teams could discuss the next direction.",
  team: [
    "Design PIC & Designer: Freddy",
    "Design Lead: Husein",
    "Product Manager: Insurance Product Manager Team",
  ],
  thumbnail: "",
  contents: [
    {
      left: {
        title: "Understanding feedback beyond complaints and NPS",
        content: (
          <div>
            <p>
              Traveloka Insurance already received customer input through
              Customer Care complaints and the main product NPS. However, these
              channels did not always help us understand which insurance
              product, feature, or moment in the journey needed improvement.
            </p>
            <p>
              Insurance also works differently from many other products. It can
              be purchased as an add-on during another booking or as a
              standalone product, and customers may only experience its value
              when they need protection or make a claim. Because of this, a
              conventional product review may not always give us the feedback
              we need.
            </p>
            <p>
              This project explored how Traveloka Insurance could build a more
              systematic feedback loop across different products, funnels, and
              customer moments—without interrupting an important transaction or
              negatively affecting sales.
            </p>
          </div>
        ),
      },
      right: {
        title: "Design question",
        content: (
          <Question>
            How might we create relevant feedback entry points across the
            insurance journey, while respecting the customer's context and
            helping the team learn what should be improved?
          </Question>
        ),
      },
      bottomContent: (
        <Hero>
          <HeroInner>
            <HeroCopy>
              <h2>How I approached the SEDAP brief</h2>
              <p>
                I started by mapping feedback needs across the insurance
                journey. I then used behavioral models to understand
                participation and translated the thinking into component
                recommendations and a governance guideline.
              </p>
            </HeroCopy>
            <HeroVisual />
          </HeroInner>
        </Hero>
      ),
    },
    {
      left: {
        title:
          "Before designing the entry points, I clarified what we needed to learn",
        content: (
          <div>
            <p>
              Complaints and broad satisfaction scores could tell the team that
              a problem existed, but they did not always show which insurance
              moment caused it or what the customer needed. I started by
              translating the design brief into more specific product
              questions.
            </p>
            <p>
              The team needed specific feedback across several funnels, but an
              additional request could interrupt the journey or frustrate the
              customer. The entry point, timing, and effort required therefore
              needed to be considered together.
            </p>
          </div>
        ),
      },
      right: {
        title: "What I needed to connect",
        content: (
          <p>
            As Design PIC and Visual Designer, I connected the business need
            with customer motivation, relevant product moments, and possible
            feedback entry points. I also organized the proposals so
            stakeholders could review and prioritize them together.
          </p>
        ),
      },
      bottomContent: <ThoughtProcess />,
    },
    {
      left: {
        title: "Why people do not respond",
        content: (
          <div>
            <p>
              It is easy to assume that people do not respond because they are
              not motivated. The Fogg Behavior Model helped me look at the
              problem more carefully: someone may want to respond but find the
              task difficult, or they may be able to respond but not see a
              reason to do it at that moment.
            </p>
            <p>
              I used the Hook Model to explore another part of the experience:
              what could encourage customers to participate again. This meant
              considering the trigger, the effort required, what customers
              received in return, and whether they wanted to continue into a
              deeper feedback activity.
            </p>
          </div>
        ),
      },
      right: {
        title: "The principle I kept",
        content: (
          <Question>
            A feedback prompt should respect the customer's situation. If the
            moment is not right, making the prompt more visible will not make
            the feedback more useful.
          </Question>
        ),
      },
      bottomContent: <BehaviorModels />,
    },
    {
      left: {
        title: "Different feedback needs required different approaches",
        content: (
          <div>
            <p>
              From the brief, I grouped the feedback needs into three areas.
              Experience feedback looked at a journey or service that had
              already happened. Product clarity feedback checked whether complex
              insurance information was understandable. Future concepts needed
              deeper participation from selected customers.
            </p>
            <p>
              Separating these needs helped me decide which interaction was more
              appropriate. A short prompt could check clarity, a tray could ask
              for more context, and a voluntary program could support longer
              research activities.
            </p>
          </div>
        ),
      },
      right: {
        title: "How the proposals were organized",
        content: (
          <ul>
            <li>Contextual prompts and progressive clarity checks</li>
            <li>Feedback inside assistance and post-service journeys</li>
            <li>Voluntary missions for deeper product research</li>
            <li>Recognition and continuation states</li>
            <li>
              Governance for purpose, moment, audience, and component choice
            </li>
          </ul>
        ),
      },
    },
    {
      left: {
        title: "Organizing the proposals for discussion and prioritization",
        content: (
          <div>
            <p>
              I organized the proposed feedback entry points by when they could
              appear, which interaction suited the moment, what the team wanted
              to learn, and which customers should see them.
            </p>
            <p>
              The voluntary feedback program remained a longer-term concept. I
              separated it from the smaller feedback components so product
              managers and business partners could discuss its priority and
              feasibility without mixing the different directions together.
            </p>
          </div>
        ),
      },
      right: {
        title: "",
        content: (
          <p>
            The behavioral models helped me move from an abstract idea into
            product proposals. Organizing the proposals in one framework also
            made it easier to explain the customer value, the business need, and
            the reasoning behind each recommendation.
          </p>
        ),
      },
      bottomContent: <RelationshipOutcome />,
    },
    {
      left: {
        title: "What I learned",
        content: (
          <div>
            <p>
              This was an exploratory project for me and remains one of my
              favorite projects. I learned how to combine desk research,
              behavioral models, and interaction design into one story instead
              of presenting them as separate pieces of work.
            </p>
            <p>
              I also learned that building trust with stakeholders is not about
              saying that designers already know how customers will behave. It
              is about making the reasoning visible, inviting the team to review
              it together, and using customer responses as something we can
              learn from together. For me, alignment is part of a designer's
              responsibility to connect customers, stakeholders, and the
              business.
            </p>
          </div>
        ),
      },
    },
  ],
} as ProjectDetailProps;
